import React, { forwardRef } from "react";
import type { ChatMessageProps } from "./types";
import { TextMessage } from "./messages/TextMessage";
import { QuoteMessage } from "./messages/QuoteMessage";
import { ContractMessage } from "./messages/ContractMessage";
import { PaymentPendingMessage } from "./messages/PaymentPendingMessage";
import { CancellationRequestMessage } from "./messages/CancellationRequestMessage";
import { SystemMessage } from "./messages/SystemMessage";

import { ReviewSubmittedMessage } from "./messages/ReviewSubmittedMessage";
import { DocumentRequestMessage } from "./messages/DocumentRequestMessage";
import {
  ContractRestrictionConfirmMessage,
} from "./messages/ContractRestrictionConfirmMessage";

// Component Registry (Strategy Pattern) removed in favor of switch-case for better type safety

/**
 * ChatMessage - 채팅 메시지 라우터 컴포넌트 (Refactored)
 *
 * **Architecture Changes**:
 * - **Strategy Pattern**: Uses a registry to map message types to specific components.
 * - **Context API**: Uses ChatActionContext to avoid prop drilling for handlers.
 * - **Composition**: Each message type is its own focused component.
 */
const ChatMessageComponent = forwardRef<HTMLDivElement, ChatMessageProps>(
  (props, ref) => {
    const { message, currentUserId } = props;
    const { senderId, createdAt, messageType } = message;

    // 1. Determine Sender Direction
    const sender: "me" | "other" = senderId === currentUserId ? "me" : "other";

    // 2. Format Time
    const time = new Date(createdAt).toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // 3. Render using switch for proper type narrowing
    const commonProps = {
      ...props,
      sender: sender as "me" | "other",
      time,
    };

    switch (messageType) {
      case "text":
        return (
          <div ref={ref}>
            <TextMessage {...commonProps} message={message} />
          </div>
        );
      case "attachment": {
        // 자동 생성된 계약서류(hiddenFromSupplier)는 supplier UI에서 렌더링하지 않는다.
        // (백엔드 HTTP/realtime 필터의 안전장치 — 방을 열어둔 supplier가 realtime chat room 채널로 받는 경우 대비)
        const attachment = message.attachment as
          | { hiddenFromSupplier?: boolean }
          | undefined;
        if (props.role === "supplier" && attachment?.hiddenFromSupplier) {
          return null;
        }
        // TextMessage handles attachment type as well in this codebase
        return (
          <div ref={ref}>
            <TextMessage {...commonProps} message={message} />
          </div>
        );
      }
      case "quote":
        return (
          <div ref={ref}>
            <QuoteMessage {...commonProps} message={message} />
          </div>
        );
      case "contract":
        return (
          <div ref={ref}>
            <ContractMessage {...commonProps} message={message} />
          </div>
        );
      case "system":
        return (
          <div ref={ref}>
            <SystemMessage {...commonProps} message={message} />
          </div>
        );
      case "payment_pending":
        return (
          <div ref={ref}>
            <PaymentPendingMessage {...commonProps} message={message} />
          </div>
        );
      case "cancellation_request":
        return (
          <div ref={ref}>
            <CancellationRequestMessage {...commonProps} message={message} />
          </div>
        );
      case "review_submitted":
        return (
          <div ref={ref}>
            <ReviewSubmittedMessage {...commonProps} message={message} />
          </div>
        );
      case "document_request":
        return (
          <div ref={ref}>
            <DocumentRequestMessage {...commonProps} message={message} />
          </div>
        );
      // 수의계약체결제한여부(135) 확인 요청 카드 — 공급사가 "예"로 제출하면 채팅방에 남는다.
      case "contract_restriction_confirm":
        return (
          <div ref={ref}>
            <ContractRestrictionConfirmMessage
              role={props.role}
              message={message}
            />
          </div>
        );
      default: {
        const unknownType: unknown = messageType;
        console.warn(
          `[ChatMessage] Unknown message type: ${String(unknownType)}`,
        );
        return null;
      }
    }
  },
);

ChatMessageComponent.displayName = "ChatMessage";

/**
 * React.memo wrapper for performance optimization
 */
export const ChatMessage = React.memo(
  ChatMessageComponent,
  (prevProps, nextProps) => {
    // DOCUMENT_REQUEST 카드는 contract_documents 상태만 바뀌고 message.updatedAt 은 그대로라
    // 기본 memo 비교로는 리렌더가 스킵된다. contractDocuments/documentRequestData 참조가
    // 바뀌면 반드시 리렌더시켜야 파일 첨부 상태가 즉시 카드에 반영된다.
    if (
      prevProps.message.messageType === "document_request" &&
      nextProps.message.messageType === "document_request"
    ) {
      const prevData = (
        prevProps.message as { documentRequestData?: unknown }
      ).documentRequestData;
      const nextData = (
        nextProps.message as { documentRequestData?: unknown }
      ).documentRequestData;
      if (prevData !== nextData) return false;
    }
    return (
      prevProps.message.id === nextProps.message.id &&
      prevProps.message.updatedAt === nextProps.message.updatedAt &&
      prevProps.message.isRead === nextProps.message.isRead &&
      prevProps.currentUserId === nextProps.currentUserId &&
      prevProps.isLatestQuote === nextProps.isLatestQuote &&
      prevProps.contractId === nextProps.contractId &&
      prevProps.hasPendingCashDeposit === nextProps.hasPendingCashDeposit
    );
  },
);
